import { useState, useMemo, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  HStack,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Textarea,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1}>
        {label}
      </Text>
      <Text fontSize="sm">{value ?? '–'}</Text>
    </Box>
  )
}

type Props = {
  isOpen: boolean
  onClose: () => void
  row: Record<string, unknown> | null
}

export function ComposeEmailModal({ isOpen, onClose, row }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [isSending, setIsSending] = useState(false)

  const requestNumber = (row?.request_number as string) ?? ''
  const toEmail = (row?.contact_email as string) ?? ''

  const defaultSubject = useMemo(
    () => (requestNumber ? `Re: Request ${requestNumber}` : ''),
    [requestNumber]
  )

  useEffect(() => {
    if (row) {
      setSubject(defaultSubject)
      setBody('')
    }
  }, [row?.id, defaultSubject])

  const effectiveSubject = subject || defaultSubject

  const description = (row?.description as string) ?? '–'

  const isBodyEmpty = useMemo(() => body.trim().length === 0, [body])

  const handleSend = async () => {
    const cleanBody = body.trim()
    if (!cleanBody || !row?.id) return
    setIsSending(true)
    try {
      await apiClient.post(`/support/my-request/${row.id}/send-email`, {
        subject: effectiveSubject.trim() || undefined,
        body: cleanBody,
      })
      showSuccess('Success', 'Email sent successfully')
      setSubject('')
      setBody('')
      onClose()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsSending(false)
    }
  }

  if (!row) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Compose Email</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box
            bg={colors.cardBg}
            borderRadius="lg"
            border="1px solid"
            borderColor={colors.border}
            overflow="hidden"
            display="flex"
            flexDir="column"
            minH={0}
          >
            {/* From / To */}
            <Box px={4} py={2}>
              <Text fontSize="sm" color={colors.textSecondary} noOfLines={1}>
                <Text as="span" fontWeight="700">
                  From:
                </Text>{' '}
                alshelpdesk@actionlabs.com.ph{' '}
                <Text as="span" mx={2} color={colors.border}>
                  •
                </Text>
                <Text as="span" fontWeight="700">
                  To:
                </Text>{' '}
                {toEmail || '–'}
              </Text>
            </Box>

            <Divider borderColor={colors.borderLight} />

            {/* 1. Subject */}
            <Box px={4} py={3}>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  color={colors.textSecondary}
                  fontWeight="700"
                  mb={1}
                >
                  Subject
                </FormLabel>
                <Input
                  size="sm"
                  h="36px"
                  fontSize="sm"
                  value={subject || defaultSubject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject"
                  bg={colors.inputBg}
                  borderColor={colors.inputBorder}
                  _hover={{ borderColor: colors.inputborderHover }}
                  _focusVisible={{ borderColor: colors.brandBlue, boxShadow: 'none' }}
                />
              </FormControl>
            </Box>

            {/* 2. Ticket request details (read-only) */}
            <Box px={4} py={3} borderTop="1px solid" borderColor={colors.borderLight}>
              <DetailRow
                label="Ticket request details"
                value={<Text fontSize="sm" whiteSpace="pre-wrap">{description}</Text>}
              />
            </Box>

            {/* 3. Message */}
            <Box px={4} pb={0} display="flex" flexDir="column" flex="1" minH={0}>
              <FormControl display="flex" flexDir="column" flex="1" minH={0}>
                <FormLabel
                  fontSize="xs"
                  color={colors.textSecondary}
                  fontWeight="700"
                  mb={1}
                >
                  Message
                </FormLabel>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your message..."
                  size="sm"
                  fontSize="sm"
                  flex="1"
                  minH="120px"
                  resize="none"
                  overflowY="auto"
                  bg={colors.inputBg}
                  borderColor={colors.inputBorder}
                  _hover={{ borderColor: colors.inputborderHover }}
                  _focusVisible={{ borderColor: colors.brandBlue, boxShadow: 'none' }}
                />
              </FormControl>

              <Box mt={4}>
                <Divider borderColor={colors.borderLight} />
                <HStack justify="flex-end" px={0} py={3}>
                  <Button
                    size="sm"
                    bg={colors.brandBlue}
                    color="white"
                    _hover={{ bg: colors.brandBlueHover }}
                    _active={{ bg: colors.brandBlueActive }}
                    isLoading={isSending}
                    loadingText="Sending"
                    onClick={handleSend}
                    isDisabled={isSending || isBodyEmpty}
                  >
                    Send
                  </Button>
                </HStack>
              </Box>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
